// Highcharts data processing for retirement calculator
import { format, subYears } from 'date-fns'
import { assetsChartTooltip } from '@/lib/RetirementCalculatorHelper'
import type { RetirementData } from './types'

export const useRetirementHighchartsProcessor = () => {
  
  const mapSeriesData = (combinedAssetsData: any) => {
    const seriesData = []
    seriesData.push({
      name: 'Net assets',
      data: Object.keys(combinedAssetsData).map((mappingYearItem) => {
        // Add offset and cash savings in the assets
        combinedAssetsData[mappingYearItem].startingValue += 0
        // Use the closing value as the value for the graph
        let value = combinedAssetsData[mappingYearItem].startingValue
        // Add total savings
        if (combinedAssetsData[mappingYearItem].debtBalance) {
          value -= combinedAssetsData[mappingYearItem].debtBalance
        }
        return {
          y: Math.floor(value),
          itemData: combinedAssetsData[mappingYearItem],
          name: 'Net assets',
        }
      }),
    })
    return seriesData
  }

  const reformatSeriesData = (
    seriesData: any[], 
    userData: RetirementData, 
    partnerData: RetirementData, 
    enablePartnerForm: boolean
  ) => {
    seriesData.map((el) => {
      el.data = el.data.map((dataEl: any) => {
        let renderUser = true
        let renderPartner = true
        if (el.name.toLowerCase() === 'super') {
          renderPartner = false
        } else if (el.name.toLowerCase() === 'partner super') {
          renderUser = false
        } else if (el.name.toLowerCase() !== 'net assets') {
          renderUser = false
          renderPartner = false
        }
        const userDOB = format(subYears(new Date(), userData.age || 0), 'yyyy-MM-dd')
        let partnerDOB = '0'
        if (partnerData.age !== null && enablePartnerForm) {
          partnerDOB = format(subYears(new Date(), partnerData.age || 0), 'yyyy-MM-dd')
        }
        const tooltip = assetsChartTooltip(
          dataEl.itemData,
          el.name,
          userDOB,
          partnerDOB,
          'You',
          'Partner',
          enablePartnerForm,
          userData.age || 0,
          partnerData.age || 0,
          renderUser,
          renderPartner,
        )

        return {
          y: dataEl.y,
          tooltip: tooltip,
        }
      })
      return el
    })
    return seriesData
  }

  const formatInitialSeriesData = (graphData: any, userData: RetirementData) => {
    const combinedAssetsData: any = {}
    const tempSeriesData = graphData.asset.map((el: any) => {
      let assetName = ''
      let yearsData = []
      let type = ''
      const userBirthYear = new Date().getFullYear() - (userData.age || 0)
      
      if (el.years) {
        yearsData = Object.keys(el.years)
          .filter((year) => Number(year) - userBirthYear <= 92)
          .map((mappingYearItem) => {
            assetName = el.years[mappingYearItem].name
            const isSuper = assetName.toLowerCase() === 'super'
            const isPartnerSuper = assetName.toLowerCase() === 'partner super'
            type = el.years[mappingYearItem].type
            
            if (isSuper || isPartnerSuper) {
              el.years[mappingYearItem].startingValue = el.years[mappingYearItem].startingValueSF
              el.years[mappingYearItem].closingValue = el.years[mappingYearItem].closingValueFull
            }

            const startingValue = el.years[mappingYearItem].startingValue
            const closingValue = el.years[mappingYearItem].closingValue
            // Annual growth can be negative!
            const annualGrowth = closingValue - startingValue

            // Already present
            if (combinedAssetsData[mappingYearItem]) {
              combinedAssetsData[mappingYearItem] = {
                startingValue: combinedAssetsData[mappingYearItem].startingValue + startingValue,
                annualGrowth: combinedAssetsData[mappingYearItem].annualGrowth + annualGrowth,
                closingValue: combinedAssetsData[mappingYearItem].closingValue + closingValue,
                debtBalance: 0,
                year: mappingYearItem,
              }
            } else {
              combinedAssetsData[mappingYearItem] = {
                startingValue: startingValue,
                annualGrowth: annualGrowth,
                closingValue: closingValue,
                debtBalance: 0,
                year: mappingYearItem,
              }
            }
            return {
              y: Math.floor(startingValue),
              itemData: el.years[mappingYearItem],
              name: assetName,
            }
          })
      }

      return {
        name: assetName,
        type: type,
        data: yearsData,
      }
    })

    return { combinedAssetsData, tempSeriesData }
  }

  const mapAllRetCalcAssetsData = (graphData: any, userData: RetirementData) => {
    const { combinedAssetsData, tempSeriesData } = formatInitialSeriesData(graphData, userData)
    const seriesData = mapSeriesData(combinedAssetsData)
    seriesData.filter((el) => el.name.toLowerCase() === 'net assets')
    tempSeriesData.forEach((el: any) => {
      seriesData.push(el)
    })

    return seriesData
  }

  return {
    mapSeriesData,
    reformatSeriesData,
    formatInitialSeriesData,
    mapAllRetCalcAssetsData,
  }
}